import React, { useContext, useEffect, useState } from 'react'
import { join } from 'lodash'
import MaskedInput from 'react-maskedinput'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES } from '../../../chart-components'
import { RestrictedPerformanceHeader } from '../../../modal-components-style'
import {
    InformationIconComponent,
    ModalField,
    CustomMeasureSelect,
    CustomDatePicker,
    PipeConnectionROPSectionHeader,
    MissingInformationSectionArea
} from '../../../modal-components'
import { dateTimeToTimestamp, dateAndTimeRegex, timestampToDateTime } from '../../../../utils/parse'
import { RawActivityEvent, RestrictedPipeMovement, SelectOptions, Uom, ValueUom } from '../../../../utils/type'
import { ModalLineContainer, RestrictedSection } from '../../ActivityModal'
import { RestrictedPipeErrors } from '../../types'
import { RESTRICTED_PERFORMANCE_COLUMN } from '../../constants'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { generateRestrictedGoalValue } from '../../../../context/utils'
import { StaticDataContext } from '../../../../context/StaticDataContext'

interface RestrictedPipesInformationProps {
    restrictedPipe: RestrictedPipeMovement
    index: number
    tubularEvent: RawActivityEvent
    deleteRestrictedPipe: (index: number) => void
    filtersAppliedToGoals: {}
    setFiltersAppliedToGoals: (filtersAppliedToGoals) => void
    errors: RestrictedPipeErrors
    speedOptions: ValueUom[] | null
    loadingSpeedOptions: boolean
    restrictedPipeMovementTypeList: SelectOptions[]
    fetchSpeedOptions: () => void
    updateCurrentRestrictedPipes: (restrictedIndex: number, key: string, newValue: any) => void
    assetFilterId?: string
    tubularsIntervalPerTime: boolean
    lengthUnitOptions: Uom[]
    setIsAddRestrictedPerformanceButtonDisabled: (addRestrictedPerformanceButtonDisabled: boolean) => void
}

export const RestrictedPipesInformation = ({
    restrictedPipe,
    index,
    tubularEvent,
    deleteRestrictedPipe,
    filtersAppliedToGoals,
    setFiltersAppliedToGoals,
    errors,
    speedOptions,
    loadingSpeedOptions,
    restrictedPipeMovementTypeList,
    fetchSpeedOptions,
    updateCurrentRestrictedPipes,
    assetFilterId,
    tubularsIntervalPerTime,
    lengthUnitOptions,
    setIsAddRestrictedPerformanceButtonDisabled
}: RestrictedPipesInformationProps) => {
    const eventStartDateTime = restrictedPipe
        ? restrictedPipe.start
            ? timestampToDateTime(restrictedPipe.start)
            : null
        : null
    const eventEndDateTime = restrictedPipe
        ? restrictedPipe.end
            ? timestampToDateTime(restrictedPipe.end)
            : null
        : null
    const [startDate, setStartDate] = useState<string | null>(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState<string | null>(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState<string | null>(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState<string | null>(eventEndDateTime?.time || null)
    const { liveTheme } = useContext(StaticDataContext)

    useEffect(() => {
        if (!!restrictedPipe.restrictedPipeMovementSpeed) {
            if (tubularsIntervalPerTime) {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedPipe.end)
            } else {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedPipe.finalMD?.value)
            }
        }
    }, [restrictedPipe.end, restrictedPipe.finalMD?.value])

    const isRestrictedEvent = event =>
        event?.restrictedPipeMovementType && !['UNRESTRICTED'].includes(event?.restrictedPipeMovementType)

    // const fieldDisabledAfterEntityStart = tubularEvent.state !== STATIC_DATA_STATES.EMPTY

    const onChangeRestrictedPipeProperty = (restrictedIndex: number, key: string, newValue: any) => {
        updateCurrentRestrictedPipes(restrictedIndex, key, newValue)
    }

    const updateRestrictedMovementGoalToEmpty = (restrictedIndex: number) => {
        onChangeRestrictedPipeProperty(restrictedIndex, 'restrictedPipeMovementSpeed', {
            speedAcronym: '',
            uom: null,
            value: null
        })
        onChangeRestrictedPipeProperty(restrictedIndex, 'restrictedPipeMovementSpeedAcronym', '')
    }

    useEffect(() => {
        const datetime = join([startDate, startHour], ' ')
        if (!startHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedPipeProperty(index, 'start', timestamp)
            }
        }
    }, [startDate, startHour])

    useEffect(() => {
        const datetime = join([endDate, endHour], ' ')
        if (!endHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedPipeProperty(index, 'end', timestamp)
            }
        }
    }, [endDate, endHour])

    useEffect(() => {
        if (assetFilterId && filtersAppliedToGoals && restrictedPipe.restrictedPipeMovementType) fetchSpeedOptions()
    }, [assetFilterId, filtersAppliedToGoals, restrictedPipe.restrictedPipeMovementType])

    return (
        <RestrictedSection>
            <div key={index}>
                <RestrictedPerformanceHeader>
                    <PipeConnectionROPSectionHeader label="Restricted Pipe Movement: " quantity={index + 1} />
                    {/* {fieldDisabledAfterEntityStart && ( */}
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                {i18n('Delete Restricted Performance')}
                            </Tooltip>
                        }
                    >
                        <i
                            className="far fa-trash-alt"
                            onClick={() => {
                                deleteRestrictedPipe(index)
                                setIsAddRestrictedPerformanceButtonDisabled(false)
                            }}
                        />
                    </OverlayTrigger>
                    {/* )} */}
                </RestrictedPerformanceHeader>
                <ModalField
                    label="Restricted Pipe Movement Type"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            // disabled={fieldDisabledAfterEntityStart}
                            options={restrictedPipeMovementTypeList.map(option => ({
                                label: i18n(option.label),
                                value: option.value
                            }))}
                            value={restrictedPipe.restrictedPipeMovementType}
                            onChange={value => {
                                const newValue = restrictedPipeMovementTypeList?.find(type => type?.value === value)

                                setFiltersAppliedToGoals(filters => ({
                                    ...filters,
                                    [RESTRICTED_PERFORMANCE_COLUMN]: newValue?.acronym
                                }))
                                onChangeRestrictedPipeProperty(index, 'restrictedPipeMovementType', newValue?.value)
                                updateRestrictedMovementGoalToEmpty(index)
                            }}
                            placeholder={
                                restrictedPipeMovementTypeList?.find(
                                    type => type?.value === restrictedPipe.restrictedPipeMovementType
                                )?.label
                            }
                            style={errors?.restrictedPipeMovementType ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label="Restricted Movement Goal"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            disabled={
                                // fieldDisabledAfterEntityStart ||
                                !isRestrictedEvent(restrictedPipe)
                            }
                            options={speedOptions?.map(option => generateRestrictedGoalValue(option))}
                            placeholder={i18n(isRestrictedEvent(restrictedPipe) ? 'Select...' : '-')}
                            value={
                                restrictedPipe.restrictedPipeMovementSpeed?.value &&
                                generateRestrictedGoalValue(
                                    restrictedPipe.restrictedPipeMovementSpeed,
                                    restrictedPipe.restrictedPipeMovementSpeedAcronym
                                )
                            }
                            onChange={restrictedPipeMovementSpeed => {
                                onChangeRestrictedPipeProperty(
                                    index,
                                    'restrictedPipeMovementSpeed',
                                    restrictedPipeMovementSpeed
                                )
                                onChangeRestrictedPipeProperty(
                                    index,
                                    'restrictedPipeMovementSpeedAcronym',
                                    restrictedPipeMovementSpeed.speedAcronym
                                )
                            }}
                            style={errors?.restrictedPipeMovementSpeed ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                {isRestrictedEvent(restrictedPipe) &&
                    speedOptions?.length === 0 &&
                    !restrictedPipe.restrictedPipeMovementSpeed?.value && (
                        <MissingInformationSectionArea
                            label="Missing information"
                            onRefresh={fetchSpeedOptions}
                            isLoading={loadingSpeedOptions}
                            LinkToRigPage={assetFilterId && `#/rig/${assetFilterId}/kpi-options`}
                        >
                            <p>
                                {i18n('There is no KPI Goal applied for this Restricted Performance')}{' '}
                                <strong>{i18n('Pipe MovementKey')}</strong>. {i18n('You must set a value by going to')}{' '}
                                <a href={assetFilterId && `#/rig/${assetFilterId}/kpi-options`} target="_blank">
                                    Rig KPI Goals
                                </a>
                                .
                            </p>
                            <p>
                                {i18n(
                                    'In the KPI Goals list, define as many Restricted Performance goals as you need for the'
                                )}{' '}
                                <strong>{i18n('KPI Pipe Moving')}</strong> {i18n('for tubular')}{' '}
                                <strong>Drill Pipe</strong>, <strong>Land String</strong> {i18n('or')}{' '}
                                <strong>Casing</strong>.
                            </p>
                            <p>
                                {i18n('After saving, return to this modal. The values will be loaded into the')}{' '}
                                <strong>{i18n('Restricted Movement Goal')}</strong> {i18n('field.')}
                            </p>
                        </MissingInformationSectionArea>
                    )}
                {tubularsIntervalPerTime && (
                    <>
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="Start Date"
                                        labelAditionalField={InformationIconComponent(
                                            "You must fill this field with a value greater than or equal to the tubular's Start Date/Hour value (when it is the first Restricted Performance) or greater than the End Date/Hour of the previous Restricted Performance.This checkbox enabled defines that the first tubular will have its depth extended to the greatest bit depth, allowing the automatic adjustment of the Final MD to the same value of Run Final MD. If disabled, there will be no classification of the tubular from the maximum bit depth."
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.start
                                                        ? {
                                                              borderColor: '#D9534F'
                                                          }
                                                        : undefined
                                                }
                                                isDisabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedPipe.restrictedPipeMovementSpeed?.value
                                                }
                                                date={isRestrictedEvent(restrictedPipe) ? startDate : null}
                                                setDate={setStartDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="Start Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.start ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedPipe.restrictedPipeMovementSpeed?.value
                                                }
                                                value={startHour}
                                                onChange={e => setStartHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="End Date"
                                        labelAditionalField={InformationIconComponent(
                                            'This field is automatically filled with the End Date/Hour from the previous tubular.'
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.end
                                                        ? {
                                                              borderColor: '#D9534F'
                                                          }
                                                        : undefined
                                                }
                                                isDisabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedPipe.restrictedPipeMovementSpeed?.value
                                                }
                                                date={isRestrictedEvent(restrictedPipe) ? endDate : null}
                                                setDate={setEndDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="End Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.end ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedPipe.restrictedPipeMovementSpeed?.value
                                                }
                                                value={endHour}
                                                onChange={e => setEndHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                    </>
                )}
                {!tubularsIntervalPerTime && (
                    <ModalLineContainer
                        style={{
                            paddingTop: '15px'
                        }}
                    >
                        <ModalField
                            label="Start MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedPipeProperty(index, 'startMD', uomValue)
                                        }}
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedPipe.restrictedPipeMovementSpeed?.value
                                        }
                                        selectedOptionValues={restrictedPipe.startMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value greater than or equal to the Tubular Start MD value and greater than the previous Restricted Pipe Movement Type End MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedPipe.restrictedPipeMovementSpeed?.value
                                        }
                                        value={restrictedPipe?.startMD?.value}
                                        onChange={e => {
                                            const startMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const startMD = { ...restrictedPipe?.startMD, value: startMDValue }
                                            onChangeRestrictedPipeProperty(index, 'startMD', startMD)
                                        }}
                                        style={
                                            errors?.startMD
                                                ? {
                                                      borderColor: '#D9534F'
                                                  }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            label="Final MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedPipeProperty(index, 'finalMD', uomValue)
                                        }}
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedPipe.restrictedPipeMovementSpeed?.value
                                        }
                                        selectedOptionValues={restrictedPipe.finalMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value less than or equal to the Run Final MD value and greater than the Start MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedPipe.restrictedPipeMovementSpeed?.value
                                        }
                                        value={restrictedPipe.finalMD?.value}
                                        onChange={e => {
                                            const finalMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const finalMD = { ...restrictedPipe?.finalMD, value: finalMDValue }
                                            onChangeRestrictedPipeProperty(index, 'finalMD', finalMD)
                                        }}
                                        style={
                                            errors?.finalMD
                                                ? {
                                                      borderColor: '#D9534F'
                                                  }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                    </ModalLineContainer>
                )}
            </div>
        </RestrictedSection>
    )
}
